export const SUPPORTED_LANGUAGES = ['en', 'pl', 'ru'] as const;

export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

const isSupportedLanguage = (code: string): code is SupportedLanguage =>
    (SUPPORTED_LANGUAGES as readonly string[]).includes(code);

// "en-US" -> "en", anything unsupported -> DEFAULT_LANGUAGE
export const normalizeLanguage = (code?: string | null): SupportedLanguage => {
    const base = (code ?? '').toLowerCase().split('-')[0];
    return isSupportedLanguage(base) ? base : DEFAULT_LANGUAGE;
};

// English lives without a prefix, so old links keep working and need no redirect.
export const languagePathPrefix = (language: SupportedLanguage): string =>
    language === DEFAULT_LANGUAGE ? '' : `/${language}`;

export interface LanguageFromPath {
    language: SupportedLanguage;
    /** "" for English, "/ru" otherwise. Feeds <BrowserRouter basename> and absolute URLs. */
    prefix: string;
    /** The same path with the prefix removed — what react-router sees and matches. */
    pathname: string;
}

const PREFIX_PATTERN = /^\/([a-z]{2})(?=\/|$)/i;

export const readLanguageFromPath = (pathname: string): LanguageFromPath => {
    const match = PREFIX_PATTERN.exec(pathname);
    const candidate = match ? match[1].toLowerCase() : '';

    if (!candidate || !isSupportedLanguage(candidate) || candidate === DEFAULT_LANGUAGE) {
        return {language: DEFAULT_LANGUAGE, prefix: '', pathname};
    }

    return {
        language: candidate,
        prefix: `/${candidate}`,
        pathname: pathname.slice(candidate.length + 1) || '/',
    };
};

/** Prefix-free path -> path in the given language; the home page keeps its slash: "/ru/". */
export const buildLanguagePath = (
    language: SupportedLanguage,
    pathname: string,
): string => `${languagePathPrefix(language)}${pathname || '/'}`;
